import { Check, type LucideIcon } from 'lucide-react'
import { useEffect, useRef, useState, type HTMLAttributes, type KeyboardEvent, type ReactNode } from 'react'
import './Dropdown.css'

/**
 * The shared dropdown/menu primitive, design.md 8.7. Underlies the account menu
 * (UserProfileDropdown). `trigger` renders the control that opens it; `children` is the
 * panel, usually <DropdownItem>s. Choosing an item closes the menu.
 */
export function Dropdown({
  trigger,
  children,
  align = 'start',
  panelClassName = '',
}: {
  trigger: (state: { open: boolean; toggle: () => void }) => ReactNode
  children: ReactNode
  align?: 'start' | 'end'
  panelClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      rootRef.current?.querySelector<HTMLElement>('[aria-expanded]')?.focus()
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKeyDown)
    panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // Arrow keys move between items (design.md 8.7).
  const onPanelKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const items = [...(panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])]
    const at = items.indexOf(document.activeElement as HTMLElement)
    const next = e.key === 'ArrowDown' ? (at + 1) % items.length : (at - 1 + items.length) % items.length
    items[next]?.focus()
  }

  return (
    <div className="dropdown-root" ref={rootRef}>
      {trigger({ open, toggle: () => setOpen((v) => !v) })}
      {open && (
        <div
          ref={panelRef}
          role="menu"
          className={`dropdown-panel ${align === 'end' ? 'align-end' : ''} ${panelClassName}`}
          onKeyDown={onPanelKeyDown}
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('[role="menuitem"]')) setOpen(false)
          }}
        >
          {children}
        </div>
      )}
    </div>
  )
}

export function DropdownItem({
  icon: Icon,
  selected,
  destructive,
  accent,
  href,
  onClick,
  children,
  ...rest
}: {
  icon?: LucideIcon
  selected?: boolean
  destructive?: boolean
  /** Accent-colored text and icon, for a row that leads somewhere special. */
  accent?: boolean
  /** Makes the item a link, for something that leaves the page (signing out). */
  href?: string
  onClick?: () => void
  children: ReactNode
} & Omit<HTMLAttributes<HTMLElement>, 'onClick'>) {
  const classNames = ['menu-item', selected ? 'selected' : '', destructive ? 'destructive' : '', accent ? 'accent' : ''].filter(Boolean).join(' ')
  const content = (
    <>
      {selected ? <Check className="check-icon" aria-hidden="true" /> : Icon ? <Icon aria-hidden="true" /> : null}
      {children}
    </>
  )
  if (href) {
    return <a className={classNames} role="menuitem" href={href} {...rest}>{content}</a>
  }
  return (
    <div
      className={classNames}
      role="menuitem"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          e.currentTarget.click()
        }
      }}
      {...rest}
    >
      {content}
    </div>
  )
}

export function DropdownDivider() {
  return <div className="menu-divider" role="separator" />
}

export function DropdownGroupLabel({ children }: { children: ReactNode }) {
  return <div className="menu-group-label">{children}</div>
}
